import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCurrency, fmtDate, formatApiError, mutateOrQueue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Check, X, Search, Trash2, ShoppingCart, ScanLine } from "lucide-react";
import BarcodeInput from "@/components/BarcodeInput";

function OrderModal({ onClose, onSaved }) {
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]); // {part, qty}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/parts", { params: q ? { q } : {} }).then(({ data }) => setParts(data));
  }, [q]);

  const add = (p) => {
    if (items.find((i) => i.part.id === p.id)) {
      setItems(items.map((i) => i.part.id === p.id ? { ...i, qty: i.qty + 1 } : i));
    } else {
      setItems([...items, { part: p, qty: 1 }]);
    }
  };

  const setQty = (id, qty) => setItems(items.map((i) => i.part.id === id ? { ...i, qty: Math.max(1, Number(qty) || 1) } : i));
  const remove = (id) => setItems(items.filter((i) => i.part.id !== id));
  const total = items.reduce((s, i) => s + i.qty * ((i.part.mrp ?? i.part.retail_price ?? 0) || 0), 0);

  const handleScan = async (code) => {
    try {
      const { data: part } = await api.get(`/parts/by-number/${encodeURIComponent(code)}`);
      if (part.stock <= 0) return toast.error(`${part.part_number} out of stock`);
      add(part);
      toast.success(`+1 ${part.part_number}`);
    } catch (err) {
      if (err?.response?.status === 404) toast.error(`No part with barcode ${code}`);
      else toast.error(formatApiError(err));
    }
  };

  const submit = async () => {
    if (items.length === 0) return toast.error("Add at least one part");
    setBusy(true);
    try {
      const res = await mutateOrQueue({
        method: "post",
        url: "/orders",
        body: {
          items: items.map((i) => ({ part_id: i.part.id, qty: i.qty })),
          note,
        },
        label: `Order · ${items.length} part(s) · ${fmtCurrency(total)}`,
      });
      if (res.queued) toast.success("Offline — order queued and will sync when you reconnect");
      else toast.success("Order placed");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="order-modal">
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="label-eyebrow">New order</div>
          <h3 className="text-xl font-semibold text-zinc-950">Place parts order</h3>
        </div>
        <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-0">
          {/* Search */}
          <div className="border-r border-zinc-200">
            <div className="p-4 border-b border-zinc-200 space-y-3">
              <div>
                <div className="label-eyebrow mb-1.5 flex items-center gap-2"><ScanLine className="w-3 h-3 text-[#002FA7]" />Barcode scan</div>
                <BarcodeInput
                  testid="order-barcode-input"
                  placeholder="Scan a part to add…"
                  hint="Scanner adds +1 of the matching part"
                  onScan={handleScan}
                  autoFocus
                />
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  data-testid="order-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Or search parts manually..."
                  className="w-full bg-white border border-zinc-300 text-sm pl-10 pr-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {parts.map((p) => (
                <button
                  key={p.id}
                  data-testid={`order-add-part-${p.part_number}`}
                  onClick={() => add(p)}
                  disabled={p.stock <= 0}
                  className="w-full text-left p-3 border-b border-zinc-100 hover:bg-zinc-50 flex justify-between items-center disabled:opacity-40"
                >
                  <div>
                    <div className="font-mono text-sm font-medium text-zinc-950">{p.part_number}</div>
                    <div className="text-xs text-zinc-500">{p.manufacturer} · {p.model} {p.year}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-semibold">{fmtCurrency((p.mrp ?? p.retail_price ?? 0))}</div>
                    <div className="text-[11px] font-mono text-zinc-500">Stock: {p.stock}</div>
                  </div>
                </button>
              ))}
              {parts.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No parts found</div>}
            </div>
          </div>

          {/* Cart */}
          <div className="flex flex-col">
            <div className="p-4 border-b border-zinc-200 label-eyebrow">Cart ({items.length})</div>
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">Add parts from the left</div>}
              {items.map((i) => (
                <div key={i.part.id} className="p-3 border-b border-zinc-100">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-mono text-sm font-medium text-zinc-950">{i.part.part_number}</div>
                      <div className="text-xs text-zinc-500">{i.part.manufacturer}</div>
                    </div>
                    <button onClick={() => remove(i.part.id)} className="text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="flex justify-between items-center">
                    <input data-testid={`order-qty-${i.part.part_number}`} type="number" min="1" max={i.part.stock} value={i.qty} onChange={(e) => setQty(i.part.id, e.target.value)} className="w-20 border border-zinc-300 px-2 py-1 font-mono text-sm" />
                    <div className="font-mono text-sm font-semibold">{fmtCurrency(i.qty * ((i.part.mrp ?? i.part.retail_price ?? 0) || 0))}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-200 p-4 space-y-3">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              <div className="flex justify-between items-center">
                <div className="label-eyebrow">Total</div>
                <div className="font-mono text-2xl font-bold" data-testid="order-total">{fmtCurrency(total)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">Cancel</button>
          <button data-testid="order-submit-button" disabled={busy} onClick={submit} className="px-4 py-2 bg-zinc-950 text-white text-sm uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50">
            {busy ? "Placing..." : "Place Order"}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUSES = ["all", "pending", "approved", "rejected"];
const STATUS_STYLES = {
  pending: "border-amber-200 bg-amber-50 text-amber-800",
  approved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  rejected: "border-rose-200 bg-rose-50 text-rose-700",
};

export default function Orders() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);

  const canPlace = ["admin", "manager", "retail_counter"].includes(user.role);
  const canApprove = ["admin", "manager"].includes(user.role);

  const load = () => {
    const params = status === "all" ? {} : { status };
    api.get("/orders", { params }).then(({ data }) => setOrders(data));
  };
  useEffect(() => { load(); }, [status]);

  const act = async (id, action) => {
    try {
      const { data } = await api.post(`/orders/${id}/${action}`);
      if (action === "approve") {
        const inv = data.invoice?.number || "created";
        const n = data.push_delivered ?? 0;
        const notif = n > 0
          ? ` · Notified retail counter (${n} device${n === 1 ? "" : "s"})`
          : "";
        toast.success(`Approved · Invoice ${inv}${notif}`);
      } else {
        toast.success("Rejected");
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="orders-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">Workflow</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Orders</h1>
          <p className="text-sm text-zinc-500 mt-2">Retail counter places orders. Manager/admin approves → invoice is generated automatically.</p>
        </div>
        {canPlace && (
          <button data-testid="new-order-button" onClick={() => setOpen(true)} className="bg-zinc-950 text-white text-sm uppercase tracking-wider font-semibold px-4 py-2.5 hover:bg-zinc-800 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New order
          </button>
        )}
      </div>

      <div className="flex gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`orders-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${status === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="brutalist-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-semibold">Date</th>
              <th className="text-left px-5 py-3 font-semibold">Placed by</th>
              <th className="text-left px-5 py-3 font-semibold">Items</th>
              <th className="text-right px-5 py-3 font-semibold">Total</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-right px-5 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500"><ShoppingCart className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No orders.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`order-row-${o.id}`}>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{fmtDate(o.created_at)}</td>
                <td className="px-5 py-3 text-zinc-950">{o.created_by_name}</td>
                <td className="px-5 py-3 text-zinc-700">
                  <span className="font-mono">{o.items.length}</span> part(s):
                  <span className="text-zinc-500 ml-1">{o.items.slice(0, 2).map((i) => i.part_number).join(", ")}{o.items.length > 2 && "..."}</span>
                  {o.availability && o.status === "pending" && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {o.availability.map((av) => (
                        <span
                          key={av.part_id}
                          data-testid={`order-avail-${o.id}-${av.part_number}`}
                          className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${av.fulfillable ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}
                          title={`${av.part_number}: need ${av.requested}, have ${av.available}`}
                        >
                          {av.part_number} · {av.available}/{av.requested}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-right font-mono font-semibold">{fmtCurrency(o.total)}</td>
                <td className="px-5 py-3">
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-1 border ${STATUS_STYLES[o.status] || "border-zinc-300 bg-white text-zinc-700"}`}>
                    {o.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-right space-x-1">
                  {o.invoice_id && (
                    <button data-testid={`order-view-invoice-${o.id}`} onClick={() => nav(`/invoices/${o.invoice_id}`)} className="text-xs uppercase tracking-wider px-2 py-1 border border-zinc-300 hover:bg-zinc-100">View invoice</button>
                  )}
                  {canApprove && o.status === "pending" && (
                    <>
                      <button data-testid={`order-approve-${o.id}`} onClick={() => act(o.id, "approve")} className="text-xs uppercase tracking-wider px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1"><Check className="w-3 h-3" />Approve</button>
                      <button data-testid={`order-reject-${o.id}`} onClick={() => act(o.id, "reject")} className="text-xs uppercase tracking-wider px-2 py-1 border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1"><X className="w-3 h-3" />Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <OrderModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
